import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Space,
  Skeleton,
  Spin,
  message,
  Card,
  Descriptions,
  Typography,
  Button,
} from "antd";
import {
  ModelSearchApiCommunicator,
  formatFullDateFromTimeStamp,
  SubmissionApiCommunicator,
  useAmplifyOrgSubscription,
} from "api";
import { ListPageLayout } from "layouts";
import { TableListCellItems } from "components/Model/TableListCellItems";

const { Title } = Typography;

/**
 * ModelSearchDetailPage Component
 * 
 * Displays detailed information about a specific model search result.
 * Shows the search parameters, results, and associated model information.
 * 
 * Features:
 * - Complete model search result information display
 * - Search result visualization
 * - Model information display
 * - Status tracking and display
 * - Real-time updates via Amplify subscriptions
 * - File information display
 * - Search metadata display
 * - No results handling with submission option
 * 
 * @returns {JSX.Element} The model search detail page component
 */
export function ModelSearchDetailPage() {
  const { partVectorSearchId } = useParams();
  const [submissionApiCommunicator] = React.useState(new SubmissionApiCommunicator());
  const [modelSearchApiCommunicator] = React.useState(new ModelSearchApiCommunicator());
  const [loading, setLoading] = React.useState(false);
  const [loadingMessage] = React.useState("Loading...");
  const [messageApi, contextHolder] = message.useMessage();
  const [modelSearchResult, setModelSearchResult] = React.useState(null);
  const [submittingForTraining, setSubmittingForTraining] = React.useState(false);

  const fetchPageData = React.useCallback(async () => {
    if (!partVectorSearchId) return;
    

    try {
      const res = await modelSearchApiCommunicator.fetch(partVectorSearchId);
      setModelSearchResult(res.data);
    } catch (err) {
      messageApi.error("Error loading model search result: " + err.message);
    }
    setLoading(false);
  }, [messageApi, modelSearchApiCommunicator, partVectorSearchId]);

  const handleSubmitForTraining = React.useCallback(async () => {
    if (!partVectorSearchId) {
      messageApi.error("No search ID available for submission");
      return;
    }
    
    setSubmittingForTraining(true);
    
    try {
      const response = await submissionApiCommunicator.submitForTraining(partVectorSearchId);
      messageApi.success("Successfully submitted for future PWAM model training");
      
      // Update the modelSearchResult with the partSearchSubmissionId from the response
      if (response.data && response.data.partSearchSubmissionId) {
        setModelSearchResult(prevResult => ({
          ...prevResult,
          partSearchSubmissionId: response.data.partSearchSubmissionId
        }));
      }
    } catch (err) {
      messageApi.error("Error submitting for training: " + err.message);
      setSubmittingForTraining(false);
    }
  }, [submissionApiCommunicator, partVectorSearchId, messageApi]);

  useAmplifyOrgSubscription((update) => {
    if (update.type === "partvectorsearch" && update.data.partVectorSearchId === partVectorSearchId) {
      fetchPageData();
    }
  });

  React.useEffect(() => {
    setLoading(true);
    fetchPageData();
  }, [fetchPageData]);

  return (
    <ListPageLayout>
      {contextHolder}
      <Space
        direction="vertical"
        size="middle"
        style={{
          display: "flex",
        }}
      >
        {loading ? (
          <span>
            <Spin /> {loadingMessage}
          </span>
        ) : (
          <div>
            <Title level={2}>Model Search Result</Title>
          </div>
        )}

        <Skeleton loading={loading}>
          {modelSearchResult && (
            <Card title="Search Result Details">
              <Descriptions column={1} bordered>
                <Descriptions.Item label="File Name">
                  {modelSearchResult.fileName}
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {formatFullDateFromTimeStamp(modelSearchResult.createdAt)}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <TableListCellItems.StatusBadge status={modelSearchResult.status} />
                </Descriptions.Item>
                <Descriptions.Item label="Printer">
                  {modelSearchResult.printerName || 'NA'}
                </Descriptions.Item>
                <Descriptions.Item label="Material">
                  {modelSearchResult.materialName || 'NA'}
                </Descriptions.Item>
              </Descriptions>
              {modelSearchResult.results && modelSearchResult.results.length > 0 ? (
                <div>
                  {modelSearchResult.results.map((result, idx) => (
                    <Card
                      key={idx}
                      type="inner"
                      title={
                        <>
                          {`Result #${idx + 1}`}
                          {typeof result.similarityScore === "number" && (
                            <span style={{ marginLeft: 16, fontWeight: 400, color: "#888" }}>
                              Similarity Score: {result.similarityScore.toFixed(2)}
                            </span>
                          )}
                        </>
                      }
                      style={{ marginBottom: 16 }}
                    >
                      <Descriptions column={1} bordered>
                        <Descriptions.Item label="Model Name">
                          {result.modelName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Thumbnail">
                          {result.thumbnailUrl ? (
                            <img
                              src={result.thumbnailUrl}
                              alt={result.modelName}
                              style={{ maxWidth: 120, maxHeight: 120 }}
                            />
                          ) : (
                            "No thumbnail"
                          )}
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card
                  type="inner"
                  style={{ 
                    marginTop: 16,
                    textAlign: 'center',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Space direction="vertical" size="middle">
                    <Typography.Text type="secondary" style={{ fontSize: '16px' }}>
                      {!modelSearchResult.partSearchSubmissionId ? (
                        <>
                          There were no results that matched this submission.<br />Would you like to submit it for inclusion in future PWAM model training?
                        </>
                      ) : (
                        "This has already been submitted for inclusion in training."
                      )}
                    </Typography.Text>
                    {!modelSearchResult.partSearchSubmissionId && (
                      <Button 
                        type="primary" 
                        onClick={handleSubmitForTraining}
                        size="large"
                        loading={submittingForTraining}
                        disabled={submittingForTraining}
                      >
                        Submit for Training
                      </Button>
                    )}
                  </Space>
                </Card>
              )}
            </Card>
          )}
        </Skeleton>
      </Space>
    </ListPageLayout>
  );
}